import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, ArrowLeft, Calendar, Users, MessageSquare, ExternalLink, Phone, Mail, FileText, Download } from 'lucide-react';
import { StepCard, InfoCard, ExampleCard } from './SectionComponents';

const SalesInterviewSupportSection = ({ onBack, onComplete, isCompleted, onNext, nextSectionTitle, hasNext }) => {

  const supportOptions = [
    {
      title: "Gemma - Roleplay and Interview Expert",
      description: "Over 20 years of experience working in startup sales, Gemma is ideally placed to help you prepare for interviews and role-play tasks in particular.",
      calendlyUrl: "https://calendly.com/gemma-jumpstart-uk/sales-interview-training",
      icon: "📞",
      specialties: [
        "Sales pitch refinement",
        "Cold call roleplay practice",
        "Objection handling techniques",
        "Mock sales scenarios"
      ]
    },
    {
      title: "Channon - Go-To-Market and Sales at VC-backed startups guru",
      description: "Led startup sales teams across the world, with specific experience in enterprise sales, SaaS, EdTech, and the broader tech sector.",
      calendlyUrl: "https://calendly.com/channon-jumpstart-uk/sales-interview-training",
      icon: "🚀",
      specialties: [
        "Roleplay practice",
        "Getting your experience to stand out",
        "Mock interview sessions",
        "Business development"
      ]
    }
  ];

  const alumniNetworkTips = [
    "Use the Alumni Tool to find people who've interviewed or worked at companies you're applying to",
    "Reach out to ask about their experience, what worked and what to expect",
    "Get insights on company culture and interview processes",
    "Learn from their successful strategies"
  ];

  const caseStudyHelp = [
    "Send your first draft to kabir@jumpstart-uk.com for feedback",
    "Feedback is usually sent within ~24 hours",
    "Only send if you've done a proper draft - don't just forward the task",
    "Show initiative by taking a first pass yourself"
  ];

  return (
    <div className="bg-white min-h-screen">
      {/* Hero Section */}
      <div className="w-full py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12"
        >
          <div className="text-center mb-16">
            <h1 className="text-6xl font-bold text-gray-900 mb-6">
              Interview practice & support
            </h1>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              We've helped countless candidates improve their performance and confidence in interviews — and the support is there for you too!
            </p>
          </div>
          <div className="flex items-center justify-center space-x-6">
            <button
              onClick={onBack}
              className="inline-flex items-center px-8 py-4 bg-white text-gray-700 rounded-2xl font-semibold hover:bg-gray-50 transition-all duration-300 shadow-lg border border-gray-200"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Resources
            </button>
            <button
              onClick={onComplete}
              className={`inline-flex items-center px-8 py-4 rounded-2xl font-semibold transition-all duration-300 shadow-lg ${
                isCompleted
                  ? 'bg-[#02B491] text-white shadow-emerald-500/30'
                  : 'bg-[#02B491] text-white hover:bg-emerald-600 shadow-emerald-500/20'
              }`}
            >
              {isCompleted ? (
                <>
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Completed
                </>
              ) : (
                'Mark as Complete'
              )}
            </button>
          </div>
        </motion.div>
      </div>

      {/* Content */}
      <div className="w-full pb-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 space-y-16">
          
          <StepCard title="Book a 1:1 prep call">
            <div className="space-y-8">
              
              <div className="grid gap-8 md:grid-cols-2">
                {supportOptions.map((option, index) => (
                  <ExampleCard
                    key={index}
                    emoji={option.icon}
                    title={option.title}
                    links={[{
                      url: option.calendlyUrl,
                      text: "Book Session",
                      icon: Calendar,
                      className: "bg-gradient-to-r from-[#02B491] to-emerald-600 text-white hover:shadow-lg hover:shadow-emerald-500/20"
                    }]}
                  >
                    <p className="text-gray-600 leading-relaxed mb-4">{option.description}</p>
                    <div className="space-y-2">
                      <h6 className="font-semibold text-gray-900 text-sm">Specialties:</h6>
                      <ul className="space-y-1">
                        {option.specialties.map((specialty, i) => (
                          <li key={i} className="flex items-start space-x-2">
                            <CheckCircle className="w-4 h-4 text-[#02B491] mt-0.5 flex-shrink-0" />
                            <span className="text-gray-600 text-sm">{specialty}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </ExampleCard>
                ))}
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6">
                <h5 className="font-bold text-gray-900 mb-3 text-lg flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-blue-600" />
                  What to expect
                </h5>
                <p className="text-gray-700 leading-relaxed">
                  Sessions are <span className="font-semibold">informal but focused</span> — come prepared with what you want to work on. 
                  You can use this network to practice your elevator pitch, get feedback on your case study or outreach copy, 
                  or do a mock 1st or final round interview.
                </p>
              </div>
            </div>
          </StepCard>


          <StepCard title="Case study helpline">
            <div className="space-y-8">
              <p className="text-gray-600 text-lg leading-relaxed">
                Once you've written the <span className="font-semibold text-gray-800">first draft of your case study</span>, 
                should you be set a task, you can send it to us for feedback.
              </p>
              
              <div className="bg-white rounded-2xl p-8 border border-gray-200">
                <div className="flex items-center gap-3 mb-6">
                  <Mail className="w-6 h-6 text-[#02B491]" />
                  <div>
                    <h5 className="font-bold text-gray-900 text-lg">Email:</h5>
                    <a href="mailto:kabir@jumpstart-uk.com" className="text-[#02B491] hover:text-emerald-600 font-medium text-lg">
                      kabir@jumpstart-uk.com
                    </a>
                  </div>
                </div>
                
                <div className="grid gap-4">
                  {caseStudyHelp.map((tip, i) => (
                    <div key={i} className="flex items-start space-x-4 bg-gray-50 rounded-xl p-4">
                      <CheckCircle className="w-5 h-5 text-[#02B491] mt-1 flex-shrink-0" />
                      <span className="text-gray-800 leading-relaxed">{tip}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="bg-green-50 border border-green-200 rounded-2xl p-6">
                <h5 className="font-bold text-gray-900 mb-3 text-lg flex items-center gap-2">
                  <Phone className="w-5 h-5 text-green-600" />
                  Remember
                </h5>
                <p className="text-gray-700 leading-relaxed">
                  You can also book in a call with our roleplay experts to practice!{' '}
                  <a
                    href="https://calendly.com/gemma-jumpstart-uk/sales-interview-training"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#02B491] hover:text-emerald-600 font-medium"
                  >
                    Book with Gemma →
                  </a>
                  {' '}or{' '}
                  <a
                    href="https://calendly.com/channon-jumpstart-uk/sales-interview-training"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#02B491] hover:text-emerald-600 font-medium"
                  >
                    Book with Channon →
                  </a>
                </p>
              </div>
            </div>
          </StepCard>

          <StepCard title="Startup Jargon Buster: Essential Reading Before Your Interview">
            <p className="text-gray-600 mb-4 text-lg leading-relaxed">
              Startups use their own language (what's PMF? MVP?) and they expect you to understand it.
            </p>
            <p className="text-gray-600 mb-4 text-lg leading-relaxed">
              This cheat sheet explains 100+ key terms across business, marketing, finance, tech, and AI, with real examples and short notes on how they relate to your interviews.
            </p>
            <p className="text-gray-600 mb-6 text-lg leading-relaxed">
              If you're preparing for a startup role, make time to read it. You don't want to walk into an interview missing the basics everyone else knows.
            </p>
            <div className="bg-white rounded-2xl p-8 border border-gray-200">
              <div className="flex items-center justify-between flex-wrap gap-6">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-[#02B491]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <FileText className="w-8 h-8 text-[#02B491]" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 text-lg">Startup Jargon Buster Guide</h4>
                    <p className="text-gray-600">PDF • Essential startup terminology explained</p>
                  </div>
                </div>
                <a
                  href="/Startup Jargon Buster 2025.11 .pdf"
                  download
                  className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-[#02B491] to-emerald-600 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-emerald-500/20 transition-all duration-300"
                >
                  <Download className="w-5 h-5 mr-2" />
                  Download PDF
                </a>
              </div>
            </div>
          </StepCard>
        </div>

        {hasNext && (
          <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 pt-12">
            <div className="text-center">
              <button
                onClick={onNext}
                className="inline-flex items-center px-8 py-4 bg-white text-[#02B491] rounded-2xl font-semibold hover:bg-gray-50 transition-all duration-300 shadow-lg border-2 border-[#02B491] hover:shadow-emerald-500/20"
              >
                Continue to: {nextSectionTitle}
                <svg className="w-5 h-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SalesInterviewSupportSection; 